/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  IFlagsmith,
  IFlagsmithFeature,
  IFlagsmithTrait,
} from '@mint-lib/flags';
import { FlagsmithProviders } from '@mint-lib/flags-envs';
import { useEvent } from '@mint-lib/hooks';

import { useService } from './useService.js';

/**
 * A hook to get flags and traits from Flagsmith with suspense
 * so that the component will suspend until the flags are loaded
 */
export function useSuspenseFlags<
  F extends string = string,
  T extends string = string,
>(
  env: keyof typeof FlagsmithProviders,
  _flags: readonly F[],
  _traits: readonly T[] = [],
): {
  [K in F]: IFlagsmithFeature;
} & {
  [K in T]: IFlagsmithTrait;
} {
  const flags = useConstant<string[]>(flagsAsArray(_flags));
  const traits = useConstant<string[]>(flagsAsArray(_traits));
  const [flagsmith, version] = useService({
    name: 'flagsmith',
    args: [env],
    suspense: true,
  } as const);
  const [events] = useService({
    name: 'flagsmithEvents',
    args: [env],
  });
  const [renderKey, setRenderKey] = useState<string>(
    getRenderKey(flagsmith, flags, traits, env),
  );
  const renderRef = useRef<string>(renderKey);
  const eventListener = useEvent(() => {
    const newRenderKey = getRenderKey(flagsmith, flags, traits, env);
    if (newRenderKey !== renderRef.current) {
      renderRef.current = newRenderKey;
      setRenderKey(newRenderKey);
    }
  });
  if (import.meta.env.DEV) {
    // @ts-ignore TS2339 we will use this in devtools
    eventListener.flags = flags;
    // @ts-ignore TS2339 we will use this in devtools
    eventListener.traits = traits;
    // @ts-ignore TS2339 we will use this in devtools
    eventListener.env = env;
  }
  useEffect(() => {
    eventListener();
    const unsubscribes: ((() => void) | undefined)[] = [];
    unsubscribes.push(events?.on('event', eventListener));
    unsubscribes.push(events?.on('loadingEvent', eventListener));
    if (import.meta.env.DEV) {
      // @ts-expect-error It's fine
      events?.emit('subscriptionAdded', eventListener);
      unsubscribes.push(() => {
        // @ts-expect-error It's fine
        events?.emit('subscriptionRemoved', eventListener);
      });
    }

    return () => {
      unsubscribes.forEach((u) => u?.());
    };
  }, [version]);
  return useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res: any = {};
    flags.forEach((k) => {
      res[k] = {
        enabled: flagsmith.hasFeature(k),
        value: flagsmith.getValue(k),
      };
    });
    traits?.forEach((v) => {
      res[v] = flagsmith.getTrait(v);
    });
    return res;
  }, [renderKey]);
}

const getRenderKey = (
  flagsmith: IFlagsmith,
  flags: string[],
  traits: string[] = [],
  env = 'default',
) => {
  return flags
    .map((k) => {
      return `${flagsmith.getValue(k)}${flagsmith.hasFeature(k)}`;
    })
    .concat(traits.map((t) => `${flagsmith.getTrait(t)}`))
    .join(',');
};

const flagsAsArray = (_flags: string | readonly string[]): string[] => {
  if (typeof _flags === 'string') {
    return [_flags];
  } else if (typeof _flags === 'object') {
    if (_flags.hasOwnProperty('length')) {
      return _flags as string[];
    }
  }
  throw new Error(
    'Flagsmith: please supply an array of strings or a single string of flag keys to useFlags',
  );
};

const useConstant = function <T>(value: T): T {
  const ref = useRef(value);
  if (!ref.current) {
    ref.current = value;
  }
  return ref.current;
};
